/**
 * The session plan (ADR 0033, amended 2026-10-08: one session, one plan).
 *
 * One card plans one session. The card is the editor: the objective, the
 * teaching outline, teaching notes and student guidance are edited in place
 * and autosave privately through the same draft machinery wherever the card
 * appears (`useUnitDraft`). Mechanics appear only as events, in plain words,
 * in the one line under Publish: a save in flight, an unconfirmed save, a
 * colleague's newer version; once they pass, that line says the plan's state.
 * Publication stays explicit: the guidance's one filled act, Publish, selects
 * the acknowledged saved revision (ADR 0030), and learners only ever read
 * what was published.
 *
 * The session page shows the card as its own tile. The course plan shows the
 * same card inside a session's row (`embedded`), and for a plan not on a session
 * yet, without a session: it then carries a title. Putting that plan on a
 * session is its row's own Task, never a field here.
 */
import { planFailureWords } from "./session-plan-content";
import { useEffect, useLayoutEffect, useMemo, useImperativeHandle, useRef, useState, type ReactNode, type Ref, type RefObject } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { MoreHorizontal } from "lucide-react";
import { Link } from "react-router";

import { WithdrawnNotice } from "@/components/courses/workspace/WorkspacePrimitives";
import { clearGuidanceRecovery, guidanceKey, readPending, readRecovery, recoveredNewPlanId } from "@/components/courses/guidance-recovery";
import { activitiesValid, draftActivities } from "@/components/courses/unit-planner";
import { MAX_OBJECTIVES, draftFromUnit, objectiveLines, useUnitDraft, type UnitDraft, type UnitDraftState } from "@/components/courses/use-unit-draft";
import { AlertDialog, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { ConflictNotice } from "@/components/ui/conflict-notice";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/contexts/AuthContext";
import type { SessionStatus } from "@/lib/api/attendance";
import { ApiError } from "@/lib/api/client";
import { courseUnitsApi, type CourseUnit } from "@/lib/api/courseUnits";
import { classTitle } from "@/lib/class-name";
import { placeholderTopic } from "@/lib/class-plan-name";
import { refreshHomeAfterAct } from "@/lib/home-query-keys";

import { ClassPlanGuidance } from "./ClassPlanGuidance";
import { ClassPlanOutline } from "./ClassPlanOutline";

export type ClassPlanControls = { focus: () => void };

export interface SessionPlanParts {
  unit: CourseUnit | null;
  state?: UnitDraftState;
  outline: ReactNode;
  notices?: ReactNode;
  planAction?: ReactNode;
  unavailable?: boolean;
  /** Why the plan isn't drawn: still reading, a failed read with its retry, or no teaching access (R1). */
  read?: "loading" | "failed" | "forbidden";
  retry?: () => void;
  /** The teaching team's private notes, edited where they are drawn in the plan. */
  teachingNotes?: ReactNode;
  saveField?: keyof UnitDraft | null;
  onEditField?: (key: keyof UnitDraft) => void;
}

/** The class a card plans, carrying what names it (`classTitle`). A `SessionWorkspace`'s session satisfies it. */
export interface ClassPlanClass {
  id: string;
  session_no: number | null;
  session_date: string;
  starts_at: string | null;
  ends_at?: string | null;
  label: string | null;
  status?: SessionStatus;
  location?: string | null;
}

/** What the card needs to know. A `SessionWorkspace` satisfies it; `session` is null for a plan on no session. */
export interface ClassPlanSubject {
  course: { id: string; timezone: string; term_open: boolean };
  session: ClassPlanClass | null;
}

type Level = 3 | 4;
type SubHeading = "h3" | "h4" | "h5";

/** How the card is laid out where it sits. */
interface View {
  presentation: "tile" | "embedded";
  /** The embedded card's own heading level; its sections sit one below. */
  level: Level;
  /** The embedded card's region name: its session, or its plan. */
  title: string;
  idPrefix: string;
  sub: SubHeading;
  /** The session workspace, for "Open session". */
  openClassHref?: string;
  publishEmphasis: "filled" | "outline";
  renderTasks?: (parts: SessionPlanParts) => ReactNode;
}

const HEADING_ID = "class-plan-heading";

function Frame({ view, action, children, headingRef }: { view: View; action?: ReactNode; children: ReactNode; headingRef?: Ref<HTMLHeadingElement> }) {
  // Standalone, the card names itself; on the session page and in a row its Tasks draw it instead.
  if (view.presentation === "tile") {
    return (
      <section id="session-plan" aria-labelledby={HEADING_ID} data-semantic="bento.planner" className="grid min-w-0 gap-4">
        <div className="flex min-h-[1.875rem] items-center gap-2">
          <h2 id={HEADING_ID} ref={headingRef} tabIndex={-1} className="min-w-0 break-words text-section font-semibold text-foreground focus-visible:rounded-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">Session plan</h2>
          {action && <div className="ms-auto flex items-center gap-1">{action}</div>}
        </div>
        {children}
      </section>
    );
  }
  // The row above already shows the session's name, so the card's own heading
  // names the region for assistive technology without repeating it on screen.
  const Heading = view.level === 3 ? "h3" : "h4";
  const headingId = `${view.idPrefix}-heading`;
  return (
    <section aria-labelledby={headingId} data-semantic="bento.planner" className="grid min-w-0 gap-4">
      <Heading id={headingId} ref={headingRef} tabIndex={-1} className="sr-only">{view.title}</Heading>
      {(view.openClassHref || action) && (
        <div className="flex min-h-[1.875rem] flex-wrap items-center justify-end gap-x-3 gap-y-1">
          {view.openClassHref && (
            <Button asChild variant="outline" size="sm" className="me-auto">
              <Link to={view.openClassHref}>Open session</Link>
            </Button>
          )}
          {action}
        </div>
      )}
      {children}
    </section>
  );
}

/** The private fields, as words, for a reader who cannot edit them. */
function Objective({ lines, view }: { lines: string[]; view: View }) {
  const [lead, ...rest] = lines;
  const Heading = view.sub;
  const labelId = view.presentation === "tile" ? "class-plan-objective-label" : `${view.idPrefix}-objective-label`;
  return (
    <section aria-labelledby={labelId} data-semantic="bento.planner.objective" className="grid gap-1">
      <Heading id={labelId} className="text-ui font-semibold text-foreground">Session objective</Heading>
      {lead ? <p className="text-body text-foreground [overflow-wrap:anywhere]">{lead}</p> : <p className="text-ui text-muted-foreground">No objective yet.</p>}
      {rest.length > 0 && <ul className="grid list-disc gap-0.5 ps-5 text-ui text-muted-foreground">{rest.map((line, index) => <li key={index}>{line}</li>)}</ul>}
    </section>
  );
}

/**
 * Where this card keeps the actor's draft and unconfirmed operation. A plan
 * the card created lives under the key it was created from (the session's, or
 * "new" for a plan on no session), so after a reload that key still holds its
 * draft and operation; otherwise the plan's own key.
 */
function recoveryKeyFor(unit: CourseUnit | null, createKey: string, keyFor: (id: string) => string) {
  if (!unit) return createKey;
  const owned = readRecovery<{ unitId?: string }>(createKey)?.unitId === unit.id || readPending(createKey)?.unitId === unit.id;
  return owned ? createKey : keyFor(unit.id);
}

export function ClassPlanCard(props: Parameters<typeof PlanCardContent>[0]) {
  return props.renderTasks ? <SessionPlanTasks {...props} /> : <PlanCardContent {...props} />;
}

/** The writer may load or fail independently of the mounted task boxes. */
function SessionPlanTasks(props: Parameters<typeof PlanCardContent>[0]) {
  const [parts, setParts] = useState<SessionPlanParts>({ unit: null, outline: null, unavailable: true,
    notices: <p role="status">Loading the session plan...</p> });
  // Reusing this element isolates owner renders from the parts it reports.
  const owner = useMemo(() => <PlanCardContent {...props} renderTasks={next => <PlanPartsReport parts={next} report={setParts} />} />, [props]);
  return <>{owner}{props.renderTasks?.(parts)}</>;
}
function PlanPartsReport({ parts, report }: { parts: SessionPlanParts; report: (parts: SessionPlanParts) => void }) {
  useLayoutEffect(() => report(parts), [parts, report]);
  return null;
}

function PlanCardContent({
  workspace,
  canEdit,
  controlsRef,
  presentation = "tile",
  headingLevel = 3,
  focusHome,
  openClassHref,
  planId,
  createdPlanId,
  onSaved,
  onDiscarded,
  publishEmphasis = "filled",
  renderTasks,
}: {
  workspace: ClassPlanSubject;
  /** The session distributes this one writer's fields across its task boxes. */
  renderTasks?: (parts: SessionPlanParts) => ReactNode;
  /** The viewer holds a plan act and the term is open. */
  canEdit: boolean;
  controlsRef?: Ref<ClassPlanControls>;
  /** `tile` on the session page; `embedded` inside a course plan row. */
  presentation?: "tile" | "embedded";
  /** The embedded card's heading level. */
  headingLevel?: Level;
  /** Where focus returns after the card acts, when it is embedded: the row that opened it. */
  focusHome?: RefObject<HTMLElement | null>;
  /** The session workspace for this session, shown as "Open session" when embedded. */
  openClassHref?: string;
  /** With no session: the plan this card edits, or "new" for a plan not created yet. */
  planId?: string;
  /** With "new": the plan this card's row already made, so the editor reopens on it after Hide, never on a blank plan. */
  createdPlanId?: string | null;
  onSaved?: (unit: CourseUnit) => void;
  onDiscarded?: (id: string) => void;
  /** Publish's weight: `outline` where the page's filled act is elsewhere (the session portal). */
  publishEmphasis?: "filled" | "outline";
}) {
  const { course, session } = workspace;
  const { me } = useAuth();
  const queryClient = useQueryClient();
  const headingRef = useRef<HTMLHeadingElement>(null);
  const [generation, setGeneration] = useState(0);
  const [createdId, setCreatedId] = useState<string | null>(null);
  const units = useQuery({ queryKey: ["course-units", course.id], queryFn: () => courseUnitsApi.list(course.id) });
  const keyFor = (id: string) => guidanceKey(me?.tenant_id ?? "", me?.user_id ?? "", course.id, id);
  const accessDenied = units.error instanceof ApiError && [401, 403, 404].includes(units.error.status);
  useEffect(() => {
    if (accessDenied) clearGuidanceRecovery(guidanceKey(me?.tenant_id ?? "", me?.user_id ?? "", course.id, ""));
  }, [accessDenied, course.id, me?.tenant_id, me?.user_id]);
  const subject = session?.id ?? planId ?? "new";
  const objectiveId = `class-plan-objective-${subject}`;
  const home = () => focusHome?.current ?? headingRef.current ?? (renderTasks ? document.getElementById("prepare-heading") : null);
  useImperativeHandle(controlsRef, () => ({
    focus: () => {
      const target = document.getElementById(objectiveId) ?? headingRef.current;
      target?.scrollIntoView({ block: "center" });
      target?.focus({ preventScroll: true });
    },
  }), [objectiveId]);

  const unit = !units.data ? null
    : session ? units.data.find(candidate => candidate.session_id === session.id) ?? null
      : planId && planId !== "new" ? units.data.find(candidate => candidate.id === planId) ?? null
        : units.data.find(candidate => candidate.id === (createdId ?? createdPlanId ?? recoveredNewPlanId(me?.tenant_id ?? "", me?.user_id ?? "", course.id))) ?? null;
  const embedded = presentation === "embedded";
  const view: View = {
    presentation,
    level: headingLevel,
    title: session ? classTitle(session, course.timezone) : unit?.topic ?? "New plan",
    idPrefix: embedded ? `class-plan-${subject}` : "class-plan",
    sub: !embedded ? "h3" : headingLevel === 3 ? "h4" : "h5",
    openClassHref: embedded && session ? openClassHref : undefined,
    publishEmphasis,
    renderTasks,
  };

  if (accessDenied && renderTasks) return renderTasks({ unit: null, outline: null, unavailable: true, read: "forbidden", notices: <WithdrawnNotice accessibleLabel="Session plan unavailable">Teaching access is no longer available. Private recovery content has been cleared.</WithdrawnNotice> });
  if (units.isPending && renderTasks) return renderTasks({ unit: null, outline: null, unavailable: true, read: "loading", notices: <p role="status">Loading the session plan...</p> });
  if (!units.data && renderTasks) return renderTasks({ unit: null, outline: null, unavailable: true, read: "failed", retry: () => void units.refetch(), notices: <WithdrawnNotice accessibleLabel="Session plan unavailable" onRetry={() => void units.refetch()} retryLabel="Try again">The session plan could not be loaded.</WithdrawnNotice> });
  if (accessDenied) {
    return <Frame view={view} headingRef={headingRef}><WithdrawnNotice accessibleLabel="Session plan unavailable">Teaching access is no longer available. Private recovery content has been cleared.</WithdrawnNotice></Frame>;
  }
  if (units.isPending) {
    return (
      <Frame view={view} headingRef={headingRef}>
        <div role="status" aria-label="Loading the session plan" className="grid gap-3">
          <Skeleton className="h-5 w-48" />
          <Skeleton className="h-32 w-full" />
        </div>
      </Frame>
    );
  }
  if (!units.data) {
    return (
      <Frame view={view} headingRef={headingRef}>
        <WithdrawnNotice accessibleLabel="Session plan unavailable" onRetry={() => void units.refetch()} retryLabel="Try again">
          The session plan could not be loaded.
        </WithdrawnNotice>
      </Frame>
    );
  }
  if (!session && planId && planId !== "new" && !unit) {
    return <Frame view={view} headingRef={headingRef}><WithdrawnNotice accessibleLabel="Plan unavailable">This plan is no longer in the course.</WithdrawnNotice></Frame>;
  }

  const editable = canEdit && (!unit || unit.can_teach !== false);
  if (!editable) return <ReadOnlyPlan workspace={workspace} unit={unit} view={view} headingRef={headingRef} timingsKey={`imam:class-plan-timings:${me?.user_id ?? ""}`} />;

  const replaceUnit = (saved: CourseUnit) => {
    queryClient.setQueryData<CourseUnit[]>(["course-units", course.id], (current = []) =>
      [...current.filter(item => item.id !== saved.id), saved].sort((left, right) => left.position - right.position));
    // Home's actor-scoped publication preview reads this course independently.
    void queryClient.invalidateQueries({ queryKey: ["course-units", course.id] });
    void queryClient.invalidateQueries({ predicate: query => query.queryKey[0] === "course-home" && query.queryKey.includes(course.id) });
    void queryClient.invalidateQueries({ queryKey: ["session-workspace"] });
    refreshHomeAfterAct(queryClient);
    if (!session && (!planId || planId === "new")) setCreatedId(saved.id);
    onSaved?.(saved);
  };
  const latest = async (id: string) => {
    const result = await units.refetch();
    // A failed refetch hands back the last successful list; that list may be
    // the very one that produced the stale revision, so it is refused.
    if (result.isError || !result.data) return null;
    return result.data.find(item => item.id === id) ?? null;
  };

  return (
    <PlanEditor
      key={`${subject}:${generation}`}
      workspace={workspace}
      view={view}
      unit={unit}
      position={Math.max(-1, ...units.data.map(item => item.position)) + 1}
      recoveryKey={recoveryKeyFor(unit, keyFor(session ? `class-${session.id}` : "new"), keyFor)}
      timingsKey={`imam:class-plan-timings:${me?.user_id ?? ""}`}
      objectiveId={objectiveId}
      headingRef={headingRef}
      home={home}
      onSaved={replaceUnit}
      onConflict={latest}
      onDiscarded={(id) => {
        queryClient.setQueryData<CourseUnit[]>(["course-units", course.id], (current = []) => current.filter(item => item.id !== id));
        void queryClient.invalidateQueries({ queryKey: ["course-units", course.id] });
        void queryClient.invalidateQueries({ queryKey: ["session-workspace"] });
        refreshHomeAfterAct(queryClient);
        setCreatedId(null);
        setGeneration(value => value + 1);
        home()?.focus();
        onDiscarded?.(id);
      }}
    />
  );
}

function ReadOnlyPlan({ workspace, unit, view, headingRef, timingsKey }: {
  workspace: ClassPlanSubject; unit: CourseUnit | null; view: View; headingRef: Ref<HTMLHeadingElement>; timingsKey: string;
}) {
  const { course, session } = workspace;
  const Heading = view.sub;
  if (view.renderTasks) return view.renderTasks({ unit, outline: unit?.activities && <ClassPlanOutline
    idPrefix={view.idPrefix} activities={draftActivities(unit.activities)} startsAt={session?.starts_at ?? null}
    endsAt={session?.ends_at ?? null} timezone={course.timezone} timingsKey={timingsKey} clock={!!session} heading="h3" /> });
  return (
    <Frame view={view} headingRef={headingRef}>
      {!course.term_open && view.presentation === "tile" && <p className="text-ui text-muted-foreground">This term is closed. The plan is a read-only record.</p>}
      {!unit ? (
        <p className="text-ui text-muted-foreground">This session has no plan yet.</p>
      ) : (
        <>
          <Objective lines={unit.learning_objectives} view={view} />
          {unit.activities && (
            <ClassPlanOutline
              idPrefix={view.idPrefix === "class-plan" && session ? `class-plan-${session.id}` : view.idPrefix}
              activities={draftActivities(unit.activities)}
              startsAt={session?.starts_at ?? null}
              endsAt={session?.ends_at ?? null}
              timezone={course.timezone}
              timingsKey={timingsKey}
              clock={!!session}
              heading={view.sub}
            />
          )}
          {unit.teaching_notes !== undefined && (
            <section aria-labelledby={`${view.idPrefix}-notes-label`} className="grid gap-1">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <Heading id={`${view.idPrefix}-notes-label`} className="text-ui font-semibold text-foreground">Teaching notes</Heading>
                <span className="text-meta text-muted-foreground">Teaching team only</span>
              </div>
              <p className="whitespace-pre-wrap text-ui text-muted-foreground">{unit.teaching_notes || "No teaching notes."}</p>
            </section>
          )}
          <ClassPlanGuidance
            idPrefix={view.idPrefix === "class-plan" && session ? `class-plan-${session.id}` : view.idPrefix}
            saved={unit}
            sessionStatus={session?.status ?? unit.session_status ?? null}
            presentation={view.presentation}
            heading={view.sub}
          />
        </>
      )}
    </Frame>
  );
}

/** Q5's enabling rule: why Publish waits, in words, or null when it may act. */
function publishBlocked(state: UnitDraftState, topicFirst = false): string | null {
  const saved = state.acknowledged;
  if (!saved) return topicFirst ? "Publishing needs a saved plan. Start with the session topic." : "Publishing needs a saved plan. Start with the session objective.";
  if (state.dirty || state.busy || state.pending) return "Publishing waits until your latest edits are saved.";
  if (state.conflict || state.comparisonUnavailable) return "Resolve the newer version first.";
  if (saved.can_publish !== true) return "The primary instructor publishes guidance. Your draft is saved.";
  if (saved.learning_objectives.length === 0) return "Add a session objective before publishing.";
  if (saved.published_revision !== null && saved.saved_revision === saved.published_revision) return "Published";
  return null;
}

/**
 * The one line under Publish. A save that has not passed is an event in plain
 * words, with Retry where one is possible; once it has, the line says what the
 * instructor needs next: Publish's reason while it waits, the receipt of this
 * card's own save while it is said (it clears itself), or the plan's
 * publication state as a word (ADR 0035, decision 6).
 */
function planLine(state: UnitDraftState, topicFirst = false): { label: string; retry?: () => void } {
  if (state.busy) return { label: state.pending?.kind === "publish" ? "Publishing…" : "Saving…" };
  if (state.pending?.kind === "publish") return { label: "The last publish was not confirmed. Retry checks whether it arrived before anything else is sent. Leaving does not cancel it.", retry: () => void state.reconcile() };
  if (state.pending) return { label: "Not saved", retry: () => void state.reconcile() };
  if (state.storageError || state.conflict || state.comparisonUnavailable) return { label: "Not saved" };
  if (state.dirty) {
    if (state.autosaveArmed) return { label: "Saving…" };
    return state.autosavePaused && state.valid ? { label: "Not saved", retry: () => void state.save() } : { label: "Not saved" };
  }
  const reason = publishBlocked(state, topicFirst);
  if (reason) return { label: reason };
  if (state.receipt && state.receipt.kind !== "publish") return { label: "Draft saved just now" };
  return { label: state.acknowledged?.publication ? "Published" : "Not published" };
}

function invalidReason(draft: UnitDraft, topicFirst = false): string | null {
  if (!draft.topic.trim()) return topicFirst ? "Add a session topic. Autosave waits until then." : "Give the plan a title. Autosave waits until then.";
  if (objectiveLines(draft.objectives).length > MAX_OBJECTIVES) return `Use at most ${MAX_OBJECTIVES} objective lines. Autosave waits until then.`;
  if (!activitiesValid(draft.activities)) return "Give each activity a title, keep its detail under 500 characters, and use whole minutes up to 1440, or leave minutes blank. Autosave waits until then.";
  return null;
}

/** A session as a choice: its day and time, then its words. */
function PlanEditor({ workspace, view, unit, position, recoveryKey: initialKey, timingsKey, objectiveId, headingRef, home, onSaved, onConflict, onDiscarded }: {
  workspace: ClassPlanSubject;
  view: View;
  unit: CourseUnit | null;
  position: number;
  recoveryKey: string;
  timingsKey: string;
  objectiveId: string;
  headingRef: RefObject<HTMLHeadingElement | null>;
  home: () => HTMLElement | null;
  onSaved: (unit: CourseUnit) => void;
  onConflict: (id: string) => Promise<CourseUnit | null>;
  onDiscarded: (id: string) => void;
}) {
  const { course, session } = workspace;
  // The recovery identity is fixed for this card's life, so the plan the
  // first edit creates keeps the draft, the pending operation and its receipt.
  const [recoveryKey] = useState(initialKey);
  const topic = session ? (session.label?.trim() || (session.starts_at ? placeholderTopic(session.starts_at, course.timezone) : "Session")).slice(0, 200) : "";
  const state = useUnitDraft({ courseId: course.id, unit, position, recoveryKey, defaults: { topic: view.renderTasks ? "" : topic, sessionId: session?.id ?? "" }, onSaved, onConflict });
  const { draft, setDraft, acknowledged, busy, pending, refused } = state;
  const [saveField, setSaveField] = useState<keyof UnitDraft | null>(null);
  const update = (patch: Partial<UnitDraft>) => setDraft(current => ({ ...current, ...patch }));
  const [discardOpen, setDiscardOpen] = useState(false);
  const [discardError, setDiscardError] = useState("");
  const [discarding, setDiscarding] = useState(false);
  const idPrefix = view.presentation === "tile" && session ? `class-plan-${session.id}` : view.idPrefix;

  const homeRef = useRef(home);
  homeRef.current = home;
  useEffect(() => {
    if (!busy && state.operationFocus.current) {
      state.operationFocus.current = false;
      if (document.activeElement === document.body) homeRef.current()?.focus();
    }
  }, [busy, state.operationFocus]);

  const line = planLine(state, !!view.renderTasks);
  const blocked = !!publishBlocked(state, !!view.renderTasks) || busy || state.autosaveArmed;
  const invalid = state.dirty ? invalidReason(draft, !!view.renderTasks) : null;
  const canDiscard = !!acknowledged && acknowledged.published_revision === null && !busy && !pending;
  const fieldRefusal = !!view.renderTasks && saveField !== null && state.dirty && state.autosavePaused && !pending && !state.conflict && !state.comparisonUnavailable && !state.storageError;
  const event = state.conflict || fieldRefusal ? "" : pending && !busy
    ? `The last ${pending.kind === "publish" ? "publish" : "save"} was not confirmed. Retry checks whether it arrived before anything else is sent. Leaving does not cancel it.`
    : planFailureWords(state.message);
  const discard = async () => {
    if (!acknowledged) return;
    setDiscarding(true); setDiscardError("");
    try {
      await courseUnitsApi.delete(course.id, acknowledged.id, acknowledged.revision);
      sessionStorage.removeItem(recoveryKey);
      setDiscardOpen(false);
      onDiscarded(acknowledged.id);
    } catch (error) {
      setDiscardError(error instanceof ApiError
        ? `${planFailureWords(error.message)} Nothing was discarded.`
        : "The plan could not be discarded. Nothing was removed.");
    } finally {
      setDiscarding(false);
    }
  };

  const action = canDiscard ? (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button type="button" variant="ghost" size="icon-sm" aria-label={session ? "More for the session plan" : "More for this plan"}><MoreHorizontal aria-hidden /></Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onSelect={() => setDiscardOpen(true)}>Discard plan</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  ) : undefined;

  const discardDialog = (
    <AlertDialog open={discardOpen} onOpenChange={setDiscardOpen}>
        <AlertDialogContent onCloseAutoFocus={event => { event.preventDefault(); home()?.focus(); }}>
          <AlertDialogTitle>{session ? "Discard this session plan?" : "Discard this plan?"}</AlertDialogTitle>
          <AlertDialogDescription>The objective, outline, notes and guidance drafts are deleted for the whole teaching team. Nothing was published, so students see no change.</AlertDialogDescription>
          {discardError && <p role="alert" className="text-ui text-destructive">{discardError}</p>}
          <div className="flex flex-wrap justify-end gap-2">
            <AlertDialogCancel>Keep plan</AlertDialogCancel>
            <Button variant="destructive" disabled={discarding} onClick={() => void discard()}>Discard plan</Button>
          </div>
        </AlertDialogContent>
      </AlertDialog>
  );

  if (view.renderTasks) return <>
    {view.renderTasks({ unit: acknowledged, state, planAction: action, saveField, onEditField: setSaveField, outline: <ClassPlanOutline
      idPrefix={idPrefix} activities={draft.activities}
      onChange={next => { setSaveField("activities"); setDraft(current => ({ ...current, activities: typeof next === "function" ? next(current.activities) : next })); }}
      editToken={JSON.stringify(draft)} startsAt={session?.starts_at ?? null} endsAt={session?.ends_at ?? null}
      timezone={course.timezone} timingsKey={timingsKey} clock={!!session} heading="h3" />,
      notices: <>
        {event && pending?.kind !== "publish" && <p role="status" className="text-ui">{event}</p>}
        {invalid && <p role="status" className="text-meta text-muted-foreground">{invalid}</p>}
        {!busy && !state.storageError && !state.conflict && !state.comparisonUnavailable && pending?.kind !== "publish" && (pending || saveField === null && state.dirty && state.autosavePaused) && <Button variant="outline" onClick={() => { if (pending) void state.reconcile(); else void state.save(); }}>Retry plan save</Button>}
        {state.storageError && <p role="alert" className="text-ui text-destructive">Browser storage is unavailable, so this plan cannot be saved safely. Enable storage, then reload.</p>}
        {state.comparisonUnavailable && <div className="grid justify-items-start gap-2"><p className="text-ui">A colleague may have saved a newer version. Your edits are kept here.</p><Button variant="outline" disabled={busy} onClick={() => void state.refreshConflict()}>Load their version</Button></div>}
        {state.conflict && <PlanConflict state={state} conflict={state.conflict} />}
        {state.adoptedExisting && <p role="status" className="text-ui">This session already had a plan. It is loaded here with your edits added.</p>}
      </>,
    })}
    {discardDialog}
  </>;

  return (
    <Frame view={view} headingRef={headingRef} action={action}>
      {state.adoptedExisting && <p role="status" className="text-ui text-foreground">This session already had a plan. It is loaded here with your edits added.</p>}
      {event && pending?.kind !== "publish" && <p role="status" className="text-ui text-foreground">{event}</p>}
      {state.storageError && <p role="alert" className="text-ui text-destructive">Browser storage is unavailable, so this plan cannot be saved safely. Enable storage, then reload.</p>}
      {state.comparisonUnavailable && (
        <div className="grid justify-items-start gap-2 rounded-control bg-status-warning-surface p-3 text-ui text-status-warning-foreground">
          <p>A colleague may have saved a newer version, and it could not be loaded. Your edits are kept here.</p>
          <Button type="button" variant="outline" size="sm" disabled={busy} onClick={() => void state.refreshConflict()}>Load their version</Button>
        </div>
      )}
      {state.conflict && <PlanConflict state={state} conflict={state.conflict} />}

      {!refused && (
        <>
          {!session && (
            <div className="grid gap-1.5">
              <Label htmlFor={`${idPrefix}-title`}>Title</Label>
              <Input
                id={`${idPrefix}-title`}
                maxLength={200}
                value={draft.topic}
                placeholder="Name this plan"
                onChange={event => update({ topic: event.target.value })}
              />
            </div>
          )}
          <div data-semantic="bento.planner.objective" className="grid gap-1.5">
            <Label htmlFor={objectiveId}>Session objective</Label>
            <Textarea
              id={objectiveId}
              rows={1}
              maxLength={6011}
              value={draft.objectives}
              placeholder="By the end of session, students can..."
              className="min-h-[var(--control-height)] resize-none text-body md:text-body"
              onChange={event => update({ objectives: event.target.value })}
            />
          </div>

          <ClassPlanOutline
            idPrefix={idPrefix}
            activities={draft.activities}
            onChange={next => setDraft(current => ({ ...current, activities: typeof next === "function" ? next(current.activities) : next }))}
            editToken={JSON.stringify(draft)}
            startsAt={session?.starts_at ?? null}
            endsAt={session?.ends_at ?? null}
            timezone={course.timezone}
            timingsKey={timingsKey}
            clock={!!session}
            heading={view.sub}
          />
          {invalid && <p role="status" className="text-meta text-muted-foreground">{invalid}</p>}

          <div data-semantic="bento.planner.notes" className="grid gap-1.5">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <Label htmlFor={`${idPrefix}-notes`}>Teaching notes</Label>
              <span className="text-meta text-muted-foreground">Teaching team only</span>
            </div>
            <Textarea
              id={`${idPrefix}-notes`}
              rows={2}
              maxLength={8000}
              value={draft.notes}
              placeholder="Prompts, reminders, or adaptations for the teaching team"
              className="min-h-[var(--control-height)] resize-none"
              onChange={event => update({ notes: event.target.value })}
            />
          </div>

          <ClassPlanGuidance
            idPrefix={idPrefix}
            saved={acknowledged}
            sessionStatus={session?.status ?? unit?.session_status ?? null}
            draft={{ preparation: draft.preparation, recap: draft.recap, followUp: draft.followUp }}
            onDraft={patch => update(patch)}
            line={line}
            blocked={blocked}
            publishing={busy && pending?.kind === "publish"}
            publishedNotice={state.receipt?.kind === "publish" && !state.dirty}
            onPublish={() => void state.publish()}
            presentation={view.presentation}
            heading={view.sub}
            publishEmphasis={view.publishEmphasis}
          />
        </>
      )}

      {discardDialog}
    </Frame>
  );
}

/** A colleague's newer version beside this viewer's kept edits, and the two ways forward. */
function PlanConflict({ state, conflict }: { state: UnitDraftState; conflict: CourseUnit }) {
  const theirs = draftFromUnit(conflict);
  const facts = [
    { label: "Session topic", theirs: theirs.topic },
    { label: "Session objective", theirs: theirs.objectives },
    { label: "Teaching outline", theirs: theirs.activities.map(activity => `${activity.label}${activity.minutes ? `, ${activity.minutes} min` : ""}`).join("\n") },
    { label: "Teaching notes", theirs: theirs.notes },
    { label: "Preparation", theirs: theirs.preparation },
    { label: "After-session recap", theirs: theirs.recap },
    { label: "Next steps", theirs: theirs.followUp },
  ];
  return (
    <ConflictNotice
      object="this plan"
      message="A colleague saved a newer version of this plan. Your edits are kept here. Compare, then keep yours or load theirs."
      facts={facts}
      disabled={state.disabled}
      onKeepMine={state.keepMine}
      onLoadTheirs={state.discardMine}
    />
  );
}
